import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import TutorSessionCardConflict from "../shared/tutor-session-card-conflict";
import { Button } from "../ui/button";
import WarningOutlineIcon from "@/assets/svgs/warning-outline-icon";

type ConflictSessionData = {
  student: {
    name: string;
    imageUrl?: string;
  };
  subjectName: string;
  deliveryMethod: "in_person" | "remote";
  startTime: number;
  endTime: number;
};

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type BufferTimeConflictSheetPayload = {
  conflicts: ConflictSessionData[];
};

export type BufferTimeConflictSheetResult = {
  acknowledged: boolean;
};

function BufferTimeConflictSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    BufferTimeConflictSheetPayload,
    BufferTimeConflictSheetResult
  >(SheetIds.BUFFER_TIME_CONFLICT);
  const { payload } = sheet ?? {};
  const conflicts: ConflictSessionData[] = payload?.conflicts || [];

  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close({
      acknowledged: true,
    });
  }, [close]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const conflictCount = conflicts.length;
  const conflictText =
    conflictCount === 1
      ? "There is one conflict with this booking."
      : `There are ${conflictCount} conflicts with this booking.`;

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetView className="bg-teachLoop-creamBase rounded-t-[20]">
        <View className="h-6" />
        <View className="mx-5 flex-row">
          <View className="flex-1" />
          <Pressable onPress={onClose} className="active:opacity-80">
            <CloseIcon
              className="size-6"
              pathClassName="stroke-teachLoop-textPrimary"
            />
          </Pressable>
        </View>
        <View className="h-6" />

        <View className="mx-5 gap-6">
          <View className="gap-2">
            <View className="flex-row items-center gap-2">
              <WarningOutlineIcon />
              <Text className="font-geologica-medium font-medium text-base text-gray-800 leading-[1.5]">
                {conflictText}
              </Text>
            </View>
            <Text className="font-geologica-regular font-normal text-base text-gray-800 leading-[1.5]">
              Try changing your buffer time settings.
            </Text>
          </View>

          <View className="gap-4">
            {conflicts.map((conflict, index) => (
              <TutorSessionCardConflict key={index} session={conflict} />
            ))}
          </View>

          <View className="pb-8">
            <View className="flex-row justify-end">
              <Button intent="primary" outline onPress={onClose}>
                <Button.Text intent="primary">Okay</Button.Text>
              </Button>
            </View>
          </View>
        </View>
      </BottomSheetView>
    </BottomSheet>
  ) : null;
}

export default BufferTimeConflictSheet;
